import type { CurrentSession } from '@elite-residence/api-client';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { businessDate } from '../dates';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { downloadFile } from '../ui/download';
import { Alert, Field, Loading, QueryFailure, readResponse } from '../ui/ui';
import { reportCode } from './reportLabels';

const text: Dictionary<
  | 'title'
  | 'branch'
  | 'asOf'
  | 'search'
  | 'occupancyExport'
  | 'exportFormat'
  | 'downloaded'
  | 'operational'
  | 'maintenance'
  | 'eligible'
  | 'occupied'
  | 'occupancyPercent'
  | 'notApplicable'
  | 'notFixed'
  | 'periodFrom'
  | 'periodTo'
  | 'periodSearch'
  | 'eligibleDays'
  | 'occupiedDays'
  | 'valueSearch'
  | 'activitySearch'
  | 'staffSearch'
  | 'staffAttribution'
  | 'staffCreated'
  | 'staffChanged'
  | 'staffRecorded'
  | 'staffVerified'
  | 'staffMaintenance'
  | 'staffContracts'
  | 'unitMaintenance'
  | 'unitOccupied'
  | 'unitEligible'
  | 'noMethod'
  | 'reversalOf'
  | 'createdBookings'
  | 'startingBookings'
  | 'overlappingBookings'
  | 'cancellations'
  | 'noShows'
  | 'earlyExits'
  | 'currentStatuses'
  | 'agreedValue'
  | 'qualifying'
  | 'drafts'
  | 'draftValue'
  | 'cancelledBookings'
  | 'noShowBookings'
  | 'rentSearch'
  | 'balanceSearch'
  | 'positiveBalances'
  | 'customerCredits'
  | 'balancesSeparate'
  | 'rentReceived'
  | 'pendingRent'
  | 'depositSearch'
  | 'depositHeld'
  | 'netDepositMovement'
  | 'depositBalanceBasis'
  | 'depositBalances'
  | 'agreedDeposit'
  | 'depositCollected'
  | 'depositRefunded'
  | 'depositRetained'
  | 'expenseSearch'
  | 'branchExpenses'
  | 'companyExpenses'
  | 'cashSearch'
  | 'cashMovement'
  | 'companyCash'
  | 'customerSearch'
  | 'repeatCustomers'
  | 'individualCustomers'
  | 'companyCustomers'
  | 'companyBookings'
  | 'originalCustomer'
  | 'masterCustomer'
  | 'bookingReference'
  | 'customerType'
  | 'source'
  | 'yes'
  | 'no'
  | 'individual'
  | 'company'
  | 'unknownSource'
  | 'maintenanceSearch'
  | 'overdue'
  | 'contractSearch'
  | 'providerAcceptance'
  | 'funnelBookings'
  | 'funnelVersions'
  | 'sendAttempts'
  | 'sentVersions'
  | 'deliveredVersions'
  | 'viewedVersions'
  | 'signedPdfFailure'
  | 'invalidatedVersions'
  | 'supersededVersions'
  | 'expiredGrants'
  | 'dateBasis'
  | 'movementStatus'
  | 'movementMethod'
  | 'movementAmount'
  | 'expenseCategory'
  | 'expensePayee'
  | 'anyValue'
  | 'agreedUnfiltered'
  | 'transactionDate'
  | 'recordedDate'
  | 'confirmedDate'
  | 'pendingNotApplicable'
  | 'extractedAt'
  | 'selectedExport'
  | 'reportToExport'
  | 'periodReport'
  | 'valueReport'
  | 'activityReport'
  | 'staffReport'
  | 'rentReport'
  | 'depositReport'
  | 'expenseReport'
  | 'cashReport'
  | 'customerReport'
  | 'maintenanceReport'
  | 'contractReport'
  | 'signedVersions'
  | 'unit'
  | 'originalExpected'
  | 'latestExpected'
  | 'closedAt'
  | 'blockingDuration'
  | 'originalVariance'
  | 'latestVariance'
  | 'expectationChanges'
  | 'noData'
  | 'failed'
> = {
  en: {
    title: 'Occupancy',
    branch: 'Branch',
    asOf: 'Business date',
    search: 'Show',
    occupancyExport: 'Download occupancy',
    exportFormat: 'Export format',
    downloaded: 'Download ready.',
    operational: 'Operational units',
    maintenance: 'Blocking maintenance',
    eligible: 'Eligible units',
    occupied: 'Occupied units',
    occupancyPercent: 'Occupancy',
    notApplicable: 'There is no occupancy percentage because no unit is eligible.',
    notFixed: 'exact ratio; no approved rounding for a percentage',
    periodFrom: 'From',
    periodTo: 'To',
    periodSearch: 'Show period',
    eligibleDays: 'Eligible unit-days',
    occupiedDays: 'Occupied unit-days',
    valueSearch: 'Show booking value',
    activitySearch: 'Show booking activity',
    staffSearch: 'Show staff activity',
    staffAttribution: 'Counts use the recorded actor, not the current assignee.',
    staffCreated: 'created',
    staffChanged: 'changes',
    staffRecorded: 'recorded',
    staffVerified: 'verified',
    staffMaintenance: 'maintenance',
    staffContracts: 'contracts',
    unitMaintenance: 'maintenance',
    unitOccupied: 'occupied',
    unitEligible: 'eligible',
    noMethod: 'No method',
    reversalOf: 'reversal of',
    createdBookings: 'Created',
    startingBookings: 'Starting',
    overlappingBookings: 'Overlapping',
    cancellations: 'Cancellations',
    noShows: 'No-shows',
    earlyExits: 'Early exits',
    currentStatuses: 'Current statuses are not limited to this period.',
    agreedValue: 'Agreed booking value',
    qualifying: 'Qualifying bookings',
    drafts: 'Drafts',
    draftValue: 'Draft value',
    cancelledBookings: 'Cancelled',
    noShowBookings: 'No-show',
    rentSearch: 'Show rent',
    balanceSearch: 'Show booking balances',
    positiveBalances: 'Positive remaining rent',
    customerCredits: 'Customer credit',
    balancesSeparate: 'These totals are not netted across bookings.',
    rentReceived: 'Rent received',
    pendingRent: 'Pending rent',
    depositSearch: 'Show deposits',
    depositHeld: 'Current deposit held',
    netDepositMovement: 'Net deposit movement in period',
    depositBalanceBasis:
      'Current confirmed balances in this Branch at extraction. Movement date, method, status and amount filters do not change these balances.',
    depositBalances: 'Current deposit balances',
    agreedDeposit: 'Agreed deposit',
    depositCollected: 'Collected',
    depositRefunded: 'Refunded',
    depositRetained: 'Retained',
    expenseSearch: 'Show expenses',
    branchExpenses: 'Branch expenses',
    companyExpenses: 'Company expenses',
    cashSearch: 'Show cash movement',
    cashMovement: 'Cash movement',
    companyCash: 'Company cash movement',
    customerSearch: 'Show customers',
    repeatCustomers: 'Repeat customers',
    individualCustomers: 'Individual customers',
    companyCustomers: 'Company customers',
    companyBookings: 'Bookings per company',
    originalCustomer: 'Original customer id',
    masterCustomer: 'Merge master id',
    bookingReference: 'Booking',
    customerType: 'Customer type',
    source: 'Acquisition source',
    yes: 'Yes',
    no: 'No',
    individual: 'Individual',
    company: 'Company',
    unknownSource: 'Unknown source',
    maintenanceSearch: 'Show maintenance',
    overdue: 'Overdue maintenance',
    contractSearch: 'Show contracts',
    providerAcceptance: 'Provider acceptance',
    funnelBookings: 'Bookings with issued versions',
    funnelVersions: 'Issued content versions',
    sendAttempts: 'Signing link requests',
    sentVersions: 'Versions with a signing link request',
    deliveredVersions: 'Versions with proven delivery',
    viewedVersions: 'Viewed versions',
    signedPdfFailure: 'Signed versions with a PDF issue',
    invalidatedVersions: 'Invalidated versions',
    supersededVersions: 'Superseded versions',
    expiredGrants: 'Expired active grants',
    signedVersions: 'Signed versions',
    selectedExport: 'Download selected report',
    reportToExport: 'Report to export',
    periodReport: 'Period occupancy',
    valueReport: 'Agreed booking value',
    activityReport: 'Booking activity',
    staffReport: 'Staff activity',
    rentReport: 'Rent movement',
    depositReport: 'Deposits',
    expenseReport: 'Expenses and supplier refunds',
    cashReport: 'Operational cash movement',
    customerReport: 'Customer activity',
    maintenanceReport: 'Maintenance',
    contractReport: 'Contract funnel',
    dateBasis: 'Finance date basis',
    movementStatus: 'Status',
    movementMethod: 'Method',
    movementAmount: 'Amount',
    expenseCategory: 'Category',
    expensePayee: 'Payee',
    anyValue: 'Any',
    agreedUnfiltered:
      'Agreed deposit counts every booking that starts in the period. Method, status and amount do not change it.',
    transactionDate: 'Transaction date',
    recordedDate: 'Recorded date',
    confirmedDate: 'Confirmed date',
    pendingNotApplicable:
      'Pending transactions have no confirmation date. Select Transaction or Recorded date to see pending amounts.',
    extractedAt: 'Extracted at',
    unit: 'Unit',
    originalExpected: 'Original expected end',
    latestExpected: 'Latest expected end',
    closedAt: 'Actual close',
    blockingDuration: 'Actual blocking duration (seconds)',
    originalVariance: 'Variance from original expectation (days)',
    latestVariance: 'Variance from latest expectation (days)',
    expectationChanges: 'Expectation revisions',
    noData: 'No Data',
    failed: 'The report could not be loaded.',
  },
  ar: {
    title: 'الإشغال',
    branch: 'الفرع',
    asOf: 'تاريخ العمل',
    search: 'عرض',
    occupancyExport: 'تنزيل الإشغال',
    exportFormat: 'صيغة التصدير',
    downloaded: 'التنزيل جاهز.',
    operational: 'الوحدات العاملة',
    maintenance: 'الصيانة المانعة',
    eligible: 'الوحدات المؤهلة',
    occupied: 'الوحدات المشغولة',
    occupancyPercent: 'الإشغال',
    notApplicable: 'لا توجد نسبة إشغال لأنه لا توجد وحدة مؤهلة.',
    notFixed: 'نسبة دقيقة؛ لا توجد قاعدة تقريب معتمدة للنسبة المئوية',
    periodFrom: 'من',
    periodTo: 'إلى',
    periodSearch: 'عرض الفترة',
    eligibleDays: 'أيام الوحدات المؤهلة',
    occupiedDays: 'أيام الوحدات المشغولة',
    valueSearch: 'عرض قيمة الحجوزات',
    activitySearch: 'عرض حركة الحجوزات',
    staffSearch: 'عرض نشاط الموظفين',
    staffAttribution: 'العدد يستخدم الفاعل المسجّل، وليس المكلّف الحالي.',
    staffCreated: 'أُنشئت',
    staffChanged: 'تغييرات',
    staffRecorded: 'سُجلت',
    staffVerified: 'تم التحقق',
    staffMaintenance: 'صيانة',
    staffContracts: 'عقود',
    unitMaintenance: 'صيانة',
    unitOccupied: 'مشغولة',
    unitEligible: 'مؤهلة',
    noMethod: 'بلا طريقة دفع',
    reversalOf: 'عكس للقيد',
    createdBookings: 'أُنشئت',
    startingBookings: 'تبدأ',
    overlappingBookings: 'تتداخل',
    cancellations: 'الإلغاءات',
    noShows: 'عدم الحضور',
    earlyExits: 'الخروج المبكر',
    currentStatuses: 'الحالات الحالية ليست محدودة بهذه الفترة.',
    agreedValue: 'قيمة الحجوزات المتفق عليها',
    qualifying: 'الحجوزات المؤهلة',
    drafts: 'المسودات',
    draftValue: 'قيمة المسودات',
    cancelledBookings: 'الملغاة',
    noShowBookings: 'عدم الحضور',
    rentSearch: 'عرض الإيجار',
    balanceSearch: 'عرض أرصدة الحجوزات',
    positiveBalances: 'الإيجار المتبقي الموجب',
    customerCredits: 'رصيد العميل',
    balancesSeparate: 'هذه المجاميع غير مقاصّة بين الحجوزات.',
    rentReceived: 'الإيجار المقبوض',
    pendingRent: 'إيجار معلّق',
    depositSearch: 'عرض التأمين',
    depositHeld: 'رصيد التأمين الحالي',
    netDepositMovement: 'صافي حركة التأمين في الفترة',
    depositBalanceBasis:
      'الأرصدة المؤكدة الحالية لهذا الفرع عند استخراج التقرير. مرشحات تاريخ الحركة والطريقة والحالة والمبلغ لا تغيّر هذه الأرصدة.',
    depositBalances: 'أرصدة التأمين الحالية',
    agreedDeposit: 'التأمين المتفق عليه',
    depositCollected: 'المحصّل',
    depositRefunded: 'المسترد',
    depositRetained: 'المحتفظ به',
    expenseSearch: 'عرض المصروفات',
    branchExpenses: 'مصروفات الفرع',
    companyExpenses: 'مصروفات الشركة',
    cashSearch: 'عرض الحركة النقدية',
    cashMovement: 'الحركة النقدية',
    companyCash: 'الحركة النقدية للشركة',
    customerSearch: 'عرض العملاء',
    repeatCustomers: 'العملاء المتكررون',
    individualCustomers: 'العملاء الأفراد',
    companyCustomers: 'الشركات',
    companyBookings: 'الحجوزات لكل شركة',
    originalCustomer: 'معرف العميل الأصلي',
    masterCustomer: 'معرف العميل الرئيسي بعد الدمج',
    bookingReference: 'الحجز',
    customerType: 'نوع العميل',
    source: 'مصدر الاستحواذ',
    yes: 'نعم',
    no: 'لا',
    individual: 'فرد',
    company: 'شركة',
    unknownSource: 'مصدر غير محدد',
    maintenanceSearch: 'عرض الصيانة',
    overdue: 'صيانة متأخرة',
    contractSearch: 'عرض العقود',
    providerAcceptance: 'قبول المزود',
    funnelBookings: 'الحجوزات ذات النسخ الصادرة',
    funnelVersions: 'نسخ المحتوى الصادرة',
    sendAttempts: 'طلبات إرسال رابط التوقيع',
    sentVersions: 'النسخ ذات طلب رابط توقيع',
    deliveredVersions: 'النسخ ذات التسليم المثبت',
    viewedVersions: 'النسخ المشاهدة',
    signedPdfFailure: 'النسخ الموقعة ذات مشكلة في الملف',
    invalidatedVersions: 'النسخ المبطلة',
    supersededVersions: 'النسخ المستبدلة',
    expiredGrants: 'روابط التوقيع الفعالة المنتهية',
    signedVersions: 'النسخ الموقعة',
    selectedExport: 'تنزيل التقرير المحدد',
    reportToExport: 'التقرير المطلوب تصديره',
    periodReport: 'إشغال الفترة',
    valueReport: 'قيمة الحجوزات المتفق عليها',
    activityReport: 'حركة الحجوزات',
    staffReport: 'نشاط الموظفين',
    rentReport: 'حركة الإيجار',
    depositReport: 'التأمين',
    expenseReport: 'المصروفات واسترداد الموردين',
    cashReport: 'الحركة النقدية التشغيلية',
    customerReport: 'نشاط العملاء',
    maintenanceReport: 'الصيانة',
    contractReport: 'مسار العقود',
    dateBasis: 'أساس تاريخ التقرير المالي',
    movementStatus: 'الحالة',
    movementMethod: 'طريقة الدفع',
    movementAmount: 'المبلغ',
    expenseCategory: 'التصنيف',
    expensePayee: 'المستفيد',
    anyValue: 'الكل',
    agreedUnfiltered: 'التأمين المتفق عليه يشمل كل حجز يبدأ في الفترة. طريقة الدفع والحالة والمبلغ لا تغيّره.',
    transactionDate: 'تاريخ الحركة',
    recordedDate: 'تاريخ التسجيل',
    confirmedDate: 'تاريخ التأكيد',
    pendingNotApplicable: 'لا يوجد تاريخ تأكيد للعمليات المعلقة. اختر تاريخ الحركة أو التسجيل لعرض المبالغ المعلقة.',
    extractedAt: 'وقت الاستخراج',
    unit: 'الوحدة',
    originalExpected: 'النهاية المتوقعة الأصلية',
    latestExpected: 'آخر نهاية متوقعة',
    closedAt: 'الإغلاق الفعلي',
    blockingDuration: 'مدة المنع الفعلية (ثواني)',
    originalVariance: 'الفرق عن التوقع الأصلي (أيام)',
    latestVariance: 'الفرق عن آخر توقع (أيام)',
    expectationChanges: 'تعديلات التوقعات',
    noData: 'لا توجد بيانات',
    failed: 'تعذر تحميل التقرير.',
  },
};

function MovementDetails({
  lines,
}: {
  readonly lines?: readonly {
    transactionId: string;
    scope: string;
    transactionType: string;
    effect: string;
    amount: string;
    methodCode: string | null;
    status: string;
    reversalOf: string | null;
  }[];
}) {
  const { locale } = useLocale();
  const t = useText(text);
  return (lines ?? []).map((line) => (
    <p key={`${line.transactionId}:${line.effect}`}>
      {`${reportCode(locale, line.scope)} ${reportCode(locale, line.transactionType)} ${reportCode(locale, line.effect)} ${line.amount} ${line.methodCode ? reportCode(locale, line.methodCode) : t.noMethod} ${reportCode(locale, line.status)}${line.reversalOf ? ` ${t.reversalOf} ${line.reversalOf}` : ''}`}
    </p>
  ));
}

export function ReportsPage({ session }: { readonly session: CurrentSession }) {
  const { locale } = useLocale();
  const client = useApiClient();
  const t = useText(text);
  const needsInventoryBranches =
    session.permissions.includes('booking.view') || session.permissions.includes('report.operational');
  const needsFinancialBranches =
    session.permissions.includes('finance.reports') || session.permissions.includes('expense.report');
  const branches = useQuery({
    queryKey: ['reports', 'branches'],
    enabled: needsInventoryBranches || needsFinancialBranches,
    queryFn: async ({ signal }) => {
      const response = needsInventoryBranches
        ? await client.listBranches({ query: { includeArchived: 'true' } }, { signal })
        : await client.listFinancialBranches({ signal });
      return readResponse(response).branches;
    },
  });
  const methods = useQuery({
    queryKey: ['finance', 'payment-methods'],
    enabled: session.permissions.includes('finance.reports') || session.permissions.includes('expense.report'),
    queryFn: async ({ signal }) => {
      const response = await client.listPaymentMethods({ signal });
      return readResponse(response).methods.filter((method) => !method.disabled);
    },
  });
  const categories = useQuery({
    queryKey: ['finance', 'expense-categories'],
    enabled: session.permissions.includes('expense.report'),
    queryFn: async ({ signal }) => {
      const response = await client.listExpenseCategories({ signal });
      return readResponse(response).categories.filter((category) => !category.disabled);
    },
  });
  const today = businessDate(new Date());
  const [searchParams] = useSearchParams();
  const presetBranch = searchParams.get('branchId') ?? '';
  const presetFrom = searchParams.get('from') ?? today;
  const presetTo = searchParams.get('to') ?? today;
  const movementPreset =
    presetBranch && searchParams.get('from') && searchParams.get('to')
      ? { branchId: presetBranch, from: presetFrom, to: presetTo, dateBasis: 'transaction_date' }
      : null;
  const presetAsOf = searchParams.get('asOf');
  const occupancyPreset =
    presetBranch && presetAsOf && session.permissions.includes('booking.view')
      ? { branchId: presetBranch, asOf: presetAsOf }
      : null;
  const [form, setForm] = useState({
    branchId: presetBranch,
    asOf: presetAsOf ?? today,
    from: presetFrom,
    to: presetTo,
  });
  const [query, setQuery] = useState<{ branchId: string; asOf: string } | null>(occupancyPreset);
  const [periodQuery, setPeriodQuery] = useState<{ branchId: string; from: string; to: string } | null>(null);
  const [valueQuery, setValueQuery] = useState<{ branchId: string; from: string; to: string } | null>(null);
  const [activityQuery, setActivityQuery] = useState<{ branchId: string; from: string; to: string } | null>(null);
  const [staffQuery, setStaffQuery] = useState<{ branchId: string; from: string; to: string } | null>(null);
  const [dateBasis, setDateBasis] = useState<'transaction_date' | 'recorded_date' | 'confirmed_date'>(
    'transaction_date',
  );
  const [movementStatus, setMovementStatus] = useState('');
  const [movementMethod, setMovementMethod] = useState('');
  const [movementAmount, setMovementAmount] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expensePayee, setExpensePayee] = useState('');
  const movementSelection = (expense: boolean) => ({
    ...(movementStatus ? { status: movementStatus } : {}),
    ...(movementMethod ? { methodCode: movementMethod } : {}),
    ...(movementAmount ? { amount: movementAmount } : {}),
    ...(expense && expenseCategory ? { categoryId: expenseCategory } : {}),
    ...(expense && expensePayee ? { payee: expensePayee } : {}),
  });
  const [rentQuery, setRentQuery] = useState<{
    branchId: string;
    from: string;
    to: string;
    dateBasis: string;
    status?: string;
    methodCode?: string;
    amount?: string;
  } | null>(session.permissions.includes('finance.reports') ? movementPreset : null);
  const [balanceQuery, setBalanceQuery] = useState<string | null>(null);
  const [depositQuery, setDepositQuery] = useState<{
    branchId: string;
    from: string;
    to: string;
    dateBasis: string;
    status?: string;
    methodCode?: string;
    amount?: string;
  } | null>(session.permissions.includes('finance.reports') ? movementPreset : null);
  const [expenseQuery, setExpenseQuery] = useState<{
    branchId: string;
    from: string;
    to: string;
    dateBasis: string;
    status?: string;
    methodCode?: string;
    amount?: string;
    categoryId?: string;
    payee?: string;
  } | null>(session.permissions.includes('expense.report') ? movementPreset : null);
  const [cashQuery, setCashQuery] = useState<{
    branchId: string;
    from: string;
    to: string;
    dateBasis: string;
    status?: string;
    methodCode?: string;
    amount?: string;
  } | null>(session.permissions.includes('finance.reports') ? movementPreset : null);
  const canExport = session.permissions.includes('export.operational') && session.permissions.includes('booking.view');
  const exportOptions = [
    { kind: 'period_occupancy', label: t.periodReport, read: 'booking.view', permission: 'export.operational' },
    { kind: 'booking_value', label: t.valueReport, read: 'booking.view', permission: 'export.operational' },
    { kind: 'booking_activity', label: t.activityReport, read: 'booking.view', permission: 'export.operational' },
    { kind: 'customers', label: t.customerReport, read: 'report.operational', permission: 'export.operational' },
    { kind: 'staff_activity', label: t.staffReport, read: 'report.operational', permission: 'export.operational' },
    { kind: 'maintenance', label: t.maintenanceReport, read: 'report.operational', permission: 'export.operational' },
    { kind: 'contracts', label: t.contractReport, read: 'report.operational', permission: 'export.operational' },
    { kind: 'rent', label: t.rentReport, read: 'finance.reports', permission: 'export.financial' },
    { kind: 'deposits', label: t.depositReport, read: 'finance.reports', permission: 'export.financial' },
    { kind: 'expenses', label: t.expenseReport, read: 'expense.report', permission: 'export.financial' },
    { kind: 'cash', label: t.cashReport, read: 'finance.reports', permission: 'export.financial' },
  ].filter((option) => session.permissions.includes(option.permission) && session.permissions.includes(option.read));
  const [exportReportKind, setExportReportKind] = useState('');
  const chosenExport =
    exportOptions.find((option) => option.kind === exportReportKind)?.kind ?? exportOptions[0]?.kind ?? '';
  const [exportNote, setExportNote] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx' | 'pdf'>('csv');
  const [exportBusy, setExportBusy] = useState(false);
  const canFinance = session.permissions.includes('finance.reports');
  const canExpenses = session.permissions.includes('expense.report');
  const canCustomers = session.permissions.includes('report.operational');
  const [customerQuery, setCustomerQuery] = useState<{ branchId: string; from: string; to: string } | null>(null);
  const [maintenanceQuery, setMaintenanceQuery] = useState<{
    branchId: string;
    asOf: string;
    from: string;
    to: string;
  } | null>(null);
  const [contractQuery, setContractQuery] = useState<{
    branchId: string;
    asOf: string;
    from: string;
    to: string;
  } | null>(null);
  const chosenBranch = form.branchId || branches.data?.[0]?.id || '';
  const occupancy = useQuery({
    queryKey: ['reports', 'occupancy', query],
    enabled: session.permissions.includes('booking.view') && query !== null,
    queryFn: async ({ signal }) => {
      if (!query) return null;
      const response = await client.currentOccupancy({ query }, { signal });
      if (response.status !== 200) throw new Error('occupancy_unavailable');
      return response.body;
    },
  });
  const period = useQuery({
    queryKey: ['reports', 'occupancy', 'period', periodQuery],
    enabled: session.permissions.includes('booking.view') && periodQuery !== null,
    queryFn: async ({ signal }) => {
      if (!periodQuery) return null;
      const response = await client.periodOccupancy({ query: periodQuery }, { signal });
      if (response.status !== 200) throw new Error('occupancy_unavailable');
      return response.body;
    },
  });
  const value = useQuery({
    queryKey: ['reports', 'bookings', valueQuery],
    enabled: session.permissions.includes('booking.view') && valueQuery !== null,
    queryFn: async ({ signal }) => {
      if (!valueQuery) return null;
      const response = await client.bookingValue({ query: valueQuery }, { signal });
      if (response.status !== 200) throw new Error('booking_value_unavailable');
      return response.body;
    },
  });
  const activity = useQuery({
    queryKey: ['reports', 'booking-activity', activityQuery],
    enabled: session.permissions.includes('booking.view') && activityQuery !== null,
    queryFn: async ({ signal }) => {
      if (!activityQuery) return null;
      const response = await client.bookingActivity({ query: activityQuery }, { signal });
      if (response.status !== 200) throw new Error('booking_activity_unavailable');
      return response.body;
    },
  });
  const staff = useQuery({
    queryKey: ['reports', 'staff-activity', staffQuery],
    enabled: session.permissions.includes('report.operational') && staffQuery !== null,
    queryFn: async ({ signal }) => {
      if (!staffQuery) return null;
      const response = await client.staffActivity({ query: staffQuery }, { signal });
      if (response.status !== 200) throw new Error('staff_activity_unavailable');
      return response.body;
    },
  });
  const rent = useQuery({
    queryKey: ['reports', 'rent', rentQuery],
    enabled: canFinance && rentQuery !== null,
    queryFn: async ({ signal }) => {
      if (!rentQuery) return null;
      const response = await client.rentMovement({ query: rentQuery }, { signal });
      if (response.status !== 200) throw new Error('rent_unavailable');
      return response.body;
    },
  });
  const balances = useQuery({
    queryKey: ['reports', 'booking-balances', balanceQuery],
    enabled: canFinance && balanceQuery !== null,
    queryFn: async ({ signal }) => {
      if (!balanceQuery) return null;
      const response = await client.bookingBalanceSummary({ query: { branchId: balanceQuery } }, { signal });
      if (response.status !== 200) throw new Error('balances_unavailable');
      return response.body;
    },
  });
  const deposits = useQuery({
    queryKey: ['reports', 'deposits', depositQuery],
    enabled: canFinance && depositQuery !== null,
    queryFn: async ({ signal }) => {
      if (!depositQuery) return null;
      const response = await client.depositMovement({ query: depositQuery }, { signal });
      if (response.status !== 200) throw new Error('deposits_unavailable');
      return response.body;
    },
  });
  const expenses = useQuery({
    queryKey: ['reports', 'expenses', expenseQuery],
    enabled: canExpenses && expenseQuery !== null,
    queryFn: async ({ signal }) => {
      if (!expenseQuery) return null;
      const response = await client.expenseMovement({ query: expenseQuery }, { signal });
      if (response.status !== 200) throw new Error('expenses_unavailable');
      return response.body;
    },
  });
  const cash = useQuery({
    queryKey: ['reports', 'cash', cashQuery],
    enabled: canFinance && cashQuery !== null,
    queryFn: async ({ signal }) => {
      if (!cashQuery) return null;
      const response = await client.cashMovement({ query: cashQuery }, { signal });
      if (response.status !== 200) throw new Error('cash_unavailable');
      return response.body;
    },
  });
  const customers = useQuery({
    queryKey: ['reports', 'customers', customerQuery],
    enabled: canCustomers && customerQuery !== null,
    queryFn: async ({ signal }) => {
      if (!customerQuery) return null;
      const response = await client.customerActivity({ query: customerQuery }, { signal });
      if (response.status !== 200) throw new Error('customers_unavailable');
      return response.body;
    },
  });
  const maintenance = useQuery({
    queryKey: ['reports', 'maintenance', maintenanceQuery],
    enabled: canCustomers && maintenanceQuery !== null,
    queryFn: async ({ signal }) => {
      if (!maintenanceQuery) return null;
      const response = await client.maintenanceReport({ query: maintenanceQuery }, { signal });
      if (response.status !== 200) throw new Error('maintenance_unavailable');
      return response.body;
    },
  });
  const contracts = useQuery({
    queryKey: ['reports', 'contracts', contractQuery],
    enabled: canCustomers && contractQuery !== null,
    queryFn: async ({ signal }) => {
      if (!contractQuery) return null;
      const response = await client.contractFunnel({ query: contractQuery }, { signal });
      if (response.status !== 200) throw new Error('contracts_unavailable');
      return response.body;
    },
  });
  return (
    <main>
      <h1>{t.title}</h1>
      <QueryFailure query={branches} />
      <QueryFailure query={methods} />
      <QueryFailure query={categories} />
      <form
        className="er-card"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery({ branchId: chosenBranch, asOf: form.asOf });
        }}
        noValidate
      >
        <Field id="oc-branch" label={t.branch}>
          <select
            id="oc-branch"
            value={chosenBranch}
            onChange={(event) => setForm({ ...form, branchId: event.target.value })}
          >
            <option value="" />
            {(branches.data ?? []).map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </Field>
        <Field id="oc-date" label={t.asOf}>
          <input
            id="oc-date"
            type="date"
            value={form.asOf}
            onChange={(event) => setForm({ ...form, asOf: event.target.value })}
          />
        </Field>
        <button className="er-button" type="submit" disabled={!chosenBranch || !form.asOf}>
          {t.search}
        </button>
        {exportOptions.length > 0 && (
          <Field id="export-format" label={t.exportFormat}>
            <select
              id="export-format"
              value={exportFormat}
              onChange={(event) => setExportFormat(event.target.value as 'csv' | 'xlsx' | 'pdf')}
            >
              <option value="csv">CSV</option>
              <option value="xlsx">Excel</option>
              <option value="pdf">PDF</option>
            </select>
          </Field>
        )}
        {canExport && (
          <button
            className="er-button"
            type="button"
            disabled={!chosenBranch || !form.asOf || exportBusy}
            onClick={() => {
              setExportBusy(true);
              setExportNote(null);
              void client
                .exportCurrentOccupancy({ query: { branchId: chosenBranch, asOf: form.asOf, format: exportFormat } })
                .then((file) => {
                  if (file.status !== 200) {
                    setExportNote(t.failed);
                    return;
                  }
                  downloadFile(file.body, `occupancy-${form.asOf}.${exportFormat}`);
                  setExportNote(t.downloaded);
                })
                .catch(() => setExportNote(t.failed))
                .finally(() => setExportBusy(false));
            }}
          >
            {t.occupancyExport}
          </button>
        )}
      </form>
      {exportNote ? <p>{exportNote}</p> : null}
      {occupancy.isPending && query ? (
        <Loading />
      ) : occupancy.isError ? (
        <Alert kind="error">{t.failed}</Alert>
      ) : occupancy.data ? (
        <section className="er-card">
          <p>
            {t.operational}: {occupancy.data.operationalUnits}
          </p>
          <p>
            {t.maintenance}: {occupancy.data.maintenanceUnits}
          </p>
          <p>
            {t.eligible}: {occupancy.data.eligibleUnits}
          </p>
          <p>
            {t.occupied}: {occupancy.data.occupiedUnits}
          </p>
          {occupancy.data.eligibleUnits === 0 ? (
            <p>{t.notApplicable}</p>
          ) : occupancy.data.occupancyPercent !== null ? (
            <p>{`${t.occupancyPercent}: ${occupancy.data.occupancyPercent}%`}</p>
          ) : (
            <p>{`${t.occupancyPercent}: ${occupancy.data.occupiedUnits} / ${occupancy.data.eligibleUnits} (${t.notFixed})`}</p>
          )}
          {(occupancy.data.units ?? []).map((unit) => (
            <p
              key={unit.unitId}
            >{`${unit.unitNumber}: ${t.unitMaintenance} ${unit.maintenance ? (locale === 'en' ? 'yes' : t.yes) : locale === 'en' ? 'no' : t.no}, ${t.unitOccupied} ${unit.occupied ? (locale === 'en' ? 'yes' : t.yes) : locale === 'en' ? 'no' : t.no}`}</p>
          ))}
        </section>
      ) : null}
      <form
        className="er-card"
        onSubmit={(event) => {
          event.preventDefault();
          setPeriodQuery({ branchId: chosenBranch, from: form.from, to: form.to });
        }}
        noValidate
      >
        <Field id="oc-from" label={t.periodFrom}>
          <input
            id="oc-from"
            type="date"
            value={form.from}
            onChange={(event) => setForm({ ...form, from: event.target.value })}
          />
        </Field>
        <Field id="oc-to" label={t.periodTo}>
          <input
            id="oc-to"
            type="date"
            value={form.to}
            onChange={(event) => setForm({ ...form, to: event.target.value })}
          />
        </Field>
        <button className="er-button" type="submit" disabled={!chosenBranch || !form.from || !form.to}>
          {t.periodSearch}
        </button>
        <button
          className="er-button"
          type="button"
          disabled={!chosenBranch || !form.from || !form.to}
          onClick={() => setValueQuery({ branchId: chosenBranch, from: form.from, to: form.to })}
        >
          {t.valueSearch}
        </button>
        <button
          className="er-button"
          type="button"
          disabled={!chosenBranch || !form.from || !form.to}
          onClick={() => setActivityQuery({ branchId: chosenBranch, from: form.from, to: form.to })}
        >
          {t.activitySearch}
        </button>
        {canCustomers && (
          <button
            className="er-button"
            type="button"
            disabled={!chosenBranch || !form.from || !form.to}
            onClick={() => setStaffQuery({ branchId: chosenBranch, from: form.from, to: form.to })}
          >
            {t.staffSearch}
          </button>
        )}
        {(canFinance || canExpenses) && (
          <>
            <Field id="report-date-basis" label={t.dateBasis}>
              <select
                id="report-date-basis"
                value={dateBasis}
                onChange={(event) => setDateBasis(event.target.value as typeof dateBasis)}
              >
                <option value="transaction_date">{t.transactionDate}</option>
                <option value="recorded_date">{t.recordedDate}</option>
                <option value="confirmed_date">{t.confirmedDate}</option>
              </select>
            </Field>
            <Field id="movement-status" label={t.movementStatus}>
              <select
                id="movement-status"
                value={movementStatus}
                onChange={(event) => setMovementStatus(event.target.value)}
              >
                <option value="">{t.anyValue}</option>
                <option value="CONFIRMED">{reportCode(locale, 'CONFIRMED')}</option>
                <option value="PENDING_VERIFICATION">{reportCode(locale, 'PENDING_VERIFICATION')}</option>
                <option value="NEEDS_CORRECTION">{reportCode(locale, 'NEEDS_CORRECTION')}</option>
                <option value="WITHDRAWN">{reportCode(locale, 'WITHDRAWN')}</option>
              </select>
            </Field>
            <Field id="movement-method" label={t.movementMethod}>
              <select
                id="movement-method"
                value={movementMethod}
                onChange={(event) => setMovementMethod(event.target.value)}
              >
                <option value="">{t.anyValue}</option>
                {(methods.data ?? []).map((method) => (
                  <option key={method.id} value={method.code}>
                    {method.labelEn}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="movement-amount" label={t.movementAmount}>
              <input
                id="movement-amount"
                value={movementAmount}
                onChange={(event) => setMovementAmount(event.target.value)}
              />
            </Field>
          </>
        )}
        {canExpenses && (
          <>
            <Field id="expense-category" label={t.expenseCategory}>
              <select
                id="expense-category"
                value={expenseCategory}
                onChange={(event) => setExpenseCategory(event.target.value)}
              >
                <option value="">{t.anyValue}</option>
                {(categories.data ?? []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.labelEn}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="expense-payee" label={t.expensePayee}>
              <input
                id="expense-payee"
                value={expensePayee}
                onChange={(event) => setExpensePayee(event.target.value)}
              />
            </Field>
          </>
        )}
        {canFinance && (
          <button
            className="er-button"
            type="button"
            disabled={!chosenBranch || !form.from || !form.to}
            onClick={() =>
              setRentQuery({
                branchId: chosenBranch,
                from: form.from,
                to: form.to,
                dateBasis,
                ...movementSelection(false),
              })
            }
          >
            {t.rentSearch}
          </button>
        )}
        {canFinance && (
          <button
            className="er-button"
            type="button"
            disabled={!chosenBranch}
            onClick={() => setBalanceQuery(chosenBranch)}
          >
            {t.balanceSearch}
          </button>
        )}
        {canFinance && (
          <button
            className="er-button"
            type="button"
            disabled={!chosenBranch || !form.from || !form.to}
            onClick={() =>
              setDepositQuery({
                branchId: chosenBranch,
                from: form.from,
                to: form.to,
                dateBasis,
                ...movementSelection(false),
              })
            }
          >
            {t.depositSearch}
          </button>
        )}
        {canExpenses && (
          <button
            className="er-button"
            type="button"
            disabled={!chosenBranch || !form.from || !form.to}
            onClick={() =>
              setExpenseQuery({
                branchId: chosenBranch,
                from: form.from,
                to: form.to,
                dateBasis,
                ...movementSelection(true),
              })
            }
          >
            {t.expenseSearch}
          </button>
        )}
        {canFinance && (
          <button
            className="er-button"
            type="button"
            disabled={!chosenBranch || !form.from || !form.to}
            onClick={() =>
              setCashQuery({
                branchId: chosenBranch,
                from: form.from,
                to: form.to,
                dateBasis,
                ...movementSelection(false),
              })
            }
          >
            {t.cashSearch}
          </button>
        )}
        {canCustomers && (
          <button
            className="er-button"
            type="button"
            disabled={!chosenBranch || !form.from || !form.to}
            onClick={() => setCustomerQuery({ branchId: chosenBranch, from: form.from, to: form.to })}
          >
            {t.customerSearch}
          </button>
        )}
        {canCustomers && (
          <button
            className="er-button"
            type="button"
            disabled={!chosenBranch || !form.asOf || !form.from || !form.to}
            onClick={() =>
              setMaintenanceQuery({ branchId: chosenBranch, asOf: form.asOf, from: form.from, to: form.to })
            }
          >
            {t.maintenanceSearch}
          </button>
        )}
        {canCustomers && (
          <button
            className="er-button"
            type="button"
            disabled={!chosenBranch || !form.asOf || !form.from || !form.to}
            onClick={() => setContractQuery({ branchId: chosenBranch, asOf: form.asOf, from: form.from, to: form.to })}
          >
            {t.contractSearch}
          </button>
        )}
      </form>
      {exportOptions.length > 0 && (
        <section className="er-card">
          <Field id="report-to-export" label={t.reportToExport}>
            <select
              id="report-to-export"
              value={chosenExport}
              onChange={(event) => setExportReportKind(event.target.value)}
            >
              {exportOptions.map((option) => (
                <option key={option.kind} value={option.kind}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <button
            className="er-button"
            type="button"
            disabled={exportBusy || !chosenBranch || !chosenExport || !form.from || !form.to || form.from >= form.to}
            onClick={() => {
              setExportBusy(true);
              setExportNote(null);
              void client
                .exportReport({
                  path: { report: chosenExport },
                  query: {
                    branchId: chosenBranch,
                    asOf: form.asOf,
                    from: form.from,
                    to: form.to,
                    format: exportFormat,
                    dateBasis,
                    ...(['rent', 'deposits', 'expenses', 'cash'].includes(chosenExport)
                      ? movementSelection(chosenExport === 'expenses')
                      : {}),
                  },
                })
                .then((file) => {
                  if (file.status !== 200) {
                    setExportNote(t.failed);
                    return;
                  }
                  downloadFile(file.body, `${chosenExport}-${form.from}.${exportFormat}`);
                  setExportNote(t.downloaded);
                })
                .catch(() => setExportNote(t.failed))
                .finally(() => setExportBusy(false));
            }}
          >
            {t.selectedExport}
          </button>
        </section>
      )}
      {period.isPending && periodQuery ? (
        <Loading />
      ) : period.isError ? (
        <Alert kind="error">{t.failed}</Alert>
      ) : period.data ? (
        <section className="er-card">
          <p>
            {t.occupiedDays}: {period.data.occupiedUnitDays}
          </p>
          <p>
            {t.eligibleDays}: {period.data.eligibleUnitDays}
          </p>
          {period.data.eligibleUnitDays === 0 ? (
            <p>{t.notApplicable}</p>
          ) : period.data.occupancyPercent !== null ? (
            <p>{`${t.occupancyPercent}: ${period.data.occupancyPercent}%`}</p>
          ) : (
            <p>{`${t.occupancyPercent}: ${period.data.occupiedUnitDays} / ${period.data.eligibleUnitDays} (${t.notFixed})`}</p>
          )}
          {(period.data.units ?? []).map((unit) => (
            <p
              key={unit.unitId}
            >{`${unit.unitNumber}: ${t.unitEligible} ${unit.eligibleDays}, ${t.unitOccupied} ${unit.occupiedDays}`}</p>
          ))}
        </section>
      ) : null}
      {value.isPending && valueQuery ? (
        <Loading />
      ) : value.isError ? (
        <Alert kind="error">{t.failed}</Alert>
      ) : value.data ? (
        <section className="er-card">
          <p>
            {t.agreedValue}: {value.data.agreedBookingValue}
          </p>
          <p>
            {t.qualifying}: {value.data.qualifyingCount}
          </p>
          <p>
            {t.drafts}: {value.data.draftCount}
          </p>
          <p>
            {t.draftValue}: {value.data.draftValue}
          </p>
          <p>
            {t.cancelledBookings}: {value.data.cancelledCount} {value.data.cancelledValue}
          </p>
          <p>
            {t.noShowBookings}: {value.data.noShowCount} {value.data.noShowValue}
          </p>
          {(value.data.bookings ?? []).map((booking) => (
            <p
              key={booking.bookingId}
            >{`${booking.displayReference} ${reportCode(locale, booking.status)} ${booking.startDate} ${booking.effectiveTotal}`}</p>
          ))}
        </section>
      ) : null}
      {activity.isPending && activityQuery ? (
        <Loading />
      ) : activity.isError ? (
        <Alert kind="error">{t.failed}</Alert>
      ) : activity.data ? (
        <section className="er-card">
          <p>{`${t.createdBookings}: ${activity.data.createdCount} (${t.drafts}: ${activity.data.createdDraftCount})`}</p>
          <p>{`${t.startingBookings}: ${activity.data.startingCount} (${t.drafts}: ${activity.data.startingDraftCount})`}</p>
          <p>{`${t.overlappingBookings}: ${activity.data.overlappingCount} (${t.drafts}: ${activity.data.overlappingDraftCount})`}</p>
          <p>{`${t.cancellations}: ${activity.data.cancellationCount}`}</p>
          <p>{`${t.noShows}: ${activity.data.noShowCount}`}</p>
          <p>{`${t.earlyExits}: ${activity.data.earlyExitCount}`}</p>
          <p>{t.currentStatuses}</p>
          <p>{`${reportCode(locale, 'Draft')} ${activity.data.currentDraft}, ${reportCode(locale, 'Reserved')} ${activity.data.currentReserved}, ${reportCode(locale, 'Active')} ${activity.data.currentActive}, ${reportCode(locale, 'Completed')} ${activity.data.currentCompleted}, ${reportCode(locale, 'Cancelled')} ${activity.data.currentCancelled}, ${reportCode(locale, 'Ended early')} ${activity.data.currentEndedEarly}, ${reportCode(locale, 'No-show')} ${activity.data.currentNoShow}`}</p>
          {(activity.data.bookings ?? []).map((booking) => (
            <p
              key={booking.bookingId}
            >{`${booking.displayReference} ${reportCode(locale, booking.status)} ${booking.startDate}${booking.created || booking.createdDraft ? ` ${reportCode(locale, 'created')}` : ''}${booking.starting || booking.startingDraft ? ` ${reportCode(locale, 'starting')}` : ''}${booking.overlapping || booking.overlappingDraft ? ` ${reportCode(locale, 'overlapping')}` : ''}`}</p>
          ))}
          {(activity.data.events ?? []).map((event) => (
            <p
              key={`${event.bookingId}-${event.kind}-${event.effectiveDate}`}
            >{`${event.displayReference} ${reportCode(locale, event.kind)} ${event.effectiveDate}`}</p>
          ))}
        </section>
      ) : null}
      {staff.isPending && staffQuery ? (
        <Loading />
      ) : staff.isError ? (
        <Alert kind="error">{t.failed}</Alert>
      ) : staff.data ? (
        <section className="er-card">
          <p>{t.staffAttribution}</p>
          {staff.data.actors.map((person) => (
            <p key={person.staffId}>
              {`${person.displayName}: ${t.staffCreated} ${person.bookingsCreated}, ${t.staffChanged} ${person.bookingChanges}, ${t.staffRecorded} ${person.paymentsRecorded}, ${t.staffVerified} ${person.paymentsVerified}, ${t.staffMaintenance} ${person.maintenanceActions}, ${t.staffContracts} ${person.contractActions}`}
            </p>
          ))}
        </section>
      ) : null}
      {rent.isPending && rentQuery ? (
        <Loading />
      ) : rent.isError ? (
        <Alert kind="error">{t.failed}</Alert>
      ) : rent.data ? (
        <section className="er-card">
          <p>
            {t.dateBasis}:{' '}
            {rent.data.dateBasis === 'recorded_date'
              ? t.recordedDate
              : rent.data.dateBasis === 'confirmed_date'
                ? t.confirmedDate
                : t.transactionDate}
          </p>
          <p>
            {t.extractedAt}: {rent.data.extractedAt}
          </p>
          <p>
            {t.rentReceived}: {rent.data.rentReceived}
          </p>
          <p>
            {t.pendingRent}: {rent.data.pendingRent ?? t.pendingNotApplicable}
          </p>
          <MovementDetails lines={rent.data.lines} />
        </section>
      ) : null}
      {balances.isPending && balanceQuery ? (
        <Loading />
      ) : balances.isError ? (
        <Alert kind="error">{t.failed}</Alert>
      ) : balances.data ? (
        <section className="er-card">
          <p>{t.balancesSeparate}</p>
          <p>{`${t.positiveBalances}: ${balances.data.positiveBalances} (${balances.data.positiveBookingCount})`}</p>
          <p>{`${t.customerCredits}: ${balances.data.customerCredits} (${balances.data.creditBookingCount})`}</p>
        </section>
      ) : null}
      {deposits.data ? (
        <section className="er-card">
          <p>
            {t.dateBasis}:{' '}
            {deposits.data.dateBasis === 'recorded_date'
              ? t.recordedDate
              : deposits.data.dateBasis === 'confirmed_date'
                ? t.confirmedDate
                : t.transactionDate}
          </p>
          <p>
            {t.extractedAt}: {deposits.data.extractedAt}
          </p>
          <p>
            {t.depositHeld}: {deposits.data.depositHeld}
          </p>
          <p>{t.depositBalanceBasis}</p>
          <p>
            {t.netDepositMovement}: {deposits.data.netDepositMovement}
          </p>
          <p>{t.agreedUnfiltered}</p>
          <h3>{t.depositBalances}</h3>
          <table className="er-table" aria-label={t.depositBalances}>
            <thead>
              <tr>
                {[
                  t.bookingReference,
                  t.agreedDeposit,
                  t.depositCollected,
                  t.depositRefunded,
                  t.depositRetained,
                  t.depositHeld,
                ].map((label) => (
                  <th key={label} scope="col">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deposits.data.depositBalances.map((balance) => (
                <tr key={balance.bookingId}>
                  <td>{balance.displayReference}</td>
                  <td>{balance.agreedDeposit ?? '—'}</td>
                  <td>{balance.collected}</td>
                  <td>{balance.refunded}</td>
                  <td>{balance.retained}</td>
                  <td>{balance.held}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <MovementDetails lines={deposits.data.lines} />
        </section>
      ) : null}
      {[deposits, expenses, cash, customers, maintenance, contracts].some((report) => report.isError) && (
        <Alert kind="error">{t.failed}</Alert>
      )}
      {[deposits, expenses, cash, customers, maintenance, contracts].some((report) => report.isFetching) && <Loading />}
      {expenses.data ? (
        <section className="er-card">
          <p>
            {t.dateBasis}:{' '}
            {expenses.data.dateBasis === 'recorded_date'
              ? t.recordedDate
              : expenses.data.dateBasis === 'confirmed_date'
                ? t.confirmedDate
                : t.transactionDate}
          </p>
          <p>
            {t.extractedAt}: {expenses.data.extractedAt}
          </p>
          <p>
            {t.branchExpenses}: {expenses.data.branchExpenses}
          </p>
          <p>
            {t.companyExpenses}: {expenses.data.companyExpenses ?? t.noData}
          </p>
          <MovementDetails lines={expenses.data.lines} />
        </section>
      ) : null}
      {cash.data ? (
        <section className="er-card">
          <p>
            {t.dateBasis}:{' '}
            {cash.data.dateBasis === 'recorded_date'
              ? t.recordedDate
              : cash.data.dateBasis === 'confirmed_date'
                ? t.confirmedDate
                : t.transactionDate}
          </p>
          <p>
            {t.extractedAt}: {cash.data.extractedAt}
          </p>
          <p>
            {t.cashMovement}: {cash.data.cashMovement}
          </p>
          <p>
            {t.companyCash}: {cash.data.companyCashMovement ?? t.noData}
          </p>
          <MovementDetails lines={cash.data.lines} />
        </section>
      ) : null}
      {customers.data ? (
        <section className="er-card">
          <h2>{t.customerReport}</h2>
          <p>
            {t.individualCustomers}: {customers.data.individualCustomers}
          </p>
          <p>
            {t.companyCustomers}: {customers.data.companyCustomers}
          </p>
          <p>
            {t.qualifying}: {customers.data.qualifyingBookings}
          </p>
          <p>
            {t.repeatCustomers}: {customers.data.repeatCustomers}
          </p>
          <p>
            {t.unknownSource}: {customers.data.unknownSourceBookings}
          </p>
          <table className="er-table" aria-label={t.source}>
            <thead>
              <tr>
                <th scope="col">{t.source}</th>
                <th scope="col">{t.qualifying}</th>
              </tr>
            </thead>
            <tbody>
              {customers.data.sources.map((source) => (
                <tr key={source.code}>
                  <td>{source.code}</td>
                  <td>{source.bookings}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="er-table" aria-label={t.companyBookings}>
            <thead>
              <tr>
                <th scope="col">{t.masterCustomer}</th>
                <th scope="col">{t.qualifying}</th>
              </tr>
            </thead>
            <tbody>
              {customers.data.companies.map((company) => (
                <tr key={company.masterCustomerId}>
                  <td dir="ltr">{company.masterCustomerId}</td>
                  <td>{company.qualifyingBookings}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="er-table" aria-label={t.customerReport}>
            <thead>
              <tr>
                {[
                  t.bookingReference,
                  t.originalCustomer,
                  t.masterCustomer,
                  t.customerType,
                  t.repeatCustomers,
                  t.source,
                ].map((label) => (
                  <th scope="col" key={label}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.data.bookings.map((booking) => (
                <tr key={booking.bookingId}>
                  <td>{booking.reference}</td>
                  <td dir="ltr">{booking.originalCustomerId}</td>
                  <td dir="ltr">{booking.masterCustomerId}</td>
                  <td>{booking.customerType === 'individual' ? t.individual : t.company}</td>
                  <td>{booking.repeat ? t.yes : t.no}</td>
                  <td>{booking.sourceCode ?? t.unknownSource}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
      {maintenance.data ? (
        <section className="er-card">
          <p>
            {t.overdue}: {maintenance.data.overdue}
          </p>
          <table className="er-table">
            <thead>
              <tr>
                {[
                  t.unit,
                  t.originalExpected,
                  t.latestExpected,
                  t.closedAt,
                  t.blockingDuration,
                  t.originalVariance,
                  t.latestVariance,
                  t.expectationChanges,
                ].map((label) => (
                  <th key={label} scope="col">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {maintenance.data.details.map((row) => (
                <tr key={row.maintenanceId}>
                  <td>{row.unitNumber}</td>
                  <td>{row.originalExpectedEndDate ?? t.noData}</td>
                  <td>{row.expectedEndDate ?? t.noData}</td>
                  <td>{row.actualClosedAt ?? t.noData}</td>
                  <td>{row.actualBlockingSeconds ?? t.noData}</td>
                  <td>{row.originalVarianceDays ?? t.noData}</td>
                  <td>{row.latestVarianceDays ?? t.noData}</td>
                  <td>
                    {row.revisions.length === 0 ? (
                      '0'
                    ) : (
                      <details>
                        <summary>
                          {t.expectationChanges}: {row.revisions.length}
                        </summary>
                        <ul>
                          {row.revisions.map((revision) => (
                            <li key={revision.id}>
                              {revision.recordedAt}: {revision.previous.expectedEndDate ?? t.noData} →{' '}
                              {revision.next.expectedEndDate ?? t.noData} · {revision.reason}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
      {contracts.data ? (
        <section className="er-card">
          {(
            [
              ['bookings', t.funnelBookings],
              ['versions', t.funnelVersions],
              ['sendAttempts', t.sendAttempts],
              ['sentVersions', t.sentVersions],
              ['deliveredVersions', t.deliveredVersions],
              ['viewedVersions', t.viewedVersions],
              ['signedWithPdfFailure', t.signedPdfFailure],
              ['invalidatedVersions', t.invalidatedVersions],
              ['supersededVersions', t.supersededVersions],
              ['expiredGrants', t.expiredGrants],
            ] as const
          ).map(([field, label]) => (
            <p key={field}>
              {label}: {contracts.data?.[field]}
            </p>
          ))}
          <p>
            {t.providerAcceptance}: {contracts.data.providerAccepted ?? t.noData}
          </p>
          <p>
            {t.signedVersions}: {contracts.data.signedVersions}
          </p>
        </section>
      ) : null}
    </main>
  );
}
